import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { CardListing } from '../components/CardListing';
import { PageLoader } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import PriceAlertModal from '../components/PriceAlertModal';
import { Heart, ShoppingBag, ArrowLeft, Trash2, TrendingDown, ArrowDownUp, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';

export const WatchlistPage = () => {
  const navigate = useNavigate();
  const { api, isAuthenticated, user } = useAuth();
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [watchedIds, setWatchedIds] = useState(new Set());
  const [sortByDrop, setSortByDrop] = useState(false);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchWatchlist();
  }, [isAuthenticated]);

  const fetchWatchlist = async () => {
    try {
      const response = await api().get('/watchlist');
      setWatchlist(response.data.watchlist);
      setWatchedIds(new Set(response.data.watchlist.map(c => c.id)));
    } catch (error) {
      console.error('Failed to fetch watchlist:', error);
      toast.error('Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  };

  const handleWatchToggle = (cardId, isNowWatched) => {
    if (!isNowWatched) {
      // Remove from local state
      setWatchlist(prev => prev.filter(c => c.id !== cardId));
      setWatchedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(cardId);
        return newSet;
      });
    }
  };

  const clearWatchlist = async () => {
    if (!confirm('Are you sure you want to clear your entire watchlist?')) return;
    
    try {
      // Delete each item
      await Promise.all(watchlist.map(card => api().delete(`/watchlist/${card.id}`)));
      setWatchlist([]);
      setWatchedIds(new Set());
      toast.success('Watchlist cleared');
    } catch (error) {
      console.error('Failed to clear watchlist:', error);
      toast.error('Failed to clear watchlist');
    }
  };

  // Sort watchlist based on toggle
  const sortedWatchlist = [...watchlist].sort((a, b) => {
    if (sortByDrop) {
      // Sort by biggest price drop (most negative delta first)
      return (a.price_delta || 0) - (b.price_delta || 0);
    }
    // Default: sort by recently added
    return new Date(b.watched_at) - new Date(a.watched_at);
  });

  // Count price drops
  const priceDropCount = watchlist.filter(c => (c.price_delta || 0) < 0).length;
  
  // Count cards with active alerts
  const alertCount = watchlist.filter(c => c.alert_enabled && (c.target_price || c.alert_on_any_drop)).length;

  const openAlertModal = (card) => {
    setSelectedCard(card);
    setAlertModalOpen(true);
  };

  if (loading) return <PageLoader />;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <Heart className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl text-white">My Watchlist</h1>
              <p className="text-zinc-400">
                {watchlist.length} {watchlist.length === 1 ? 'card' : 'cards'} saved
                {priceDropCount > 0 && (
                  <span className="ml-2 text-emerald-400">
                    • {priceDropCount} with price drops
                  </span>
                )}
                {alertCount > 0 && (
                  <span className="ml-2 text-amber-400">
                    • {alertCount} alerts active
                  </span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Sort by Biggest Drop Toggle */}
            {watchlist.length > 1 && (
              <Button
                onClick={() => setSortByDrop(!sortByDrop)}
                variant="outline"
                className={`transition-all ${
                  sortByDrop 
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' 
                    : 'border-zinc-700 text-zinc-400 hover:text-white'
                }`}
                data-testid="sort-by-drop-toggle"
              >
                {sortByDrop ? (
                  <TrendingDown className="w-4 h-4 mr-2" />
                ) : (
                  <ArrowDownUp className="w-4 h-4 mr-2" />
                )}
                {sortByDrop ? 'Biggest Drops First' : 'Sort by Drop'}
              </Button>
            )}
            
            {watchlist.length > 0 && (
              <Button
                onClick={clearWatchlist}
                variant="outline"
                className="border-red-600/50 text-red-400 hover:bg-red-600/20 hover:border-red-500"
                data-testid="clear-watchlist-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            )}
          </div>
        </div>

        {/* Watchlist Grid or Empty State */}
        {watchlist.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-zinc-800/50 flex items-center justify-center">
              <Heart className="w-10 h-10 text-zinc-600" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Your watchlist is empty</h3>
            <p className="text-zinc-400 mb-8 max-w-md mx-auto">
              Save cards you're interested in by clicking the heart icon on any listing. 
              They'll appear here for easy access.
            </p>
            <Link to="/marketplace">
              <Button className="bg-blue-600 hover:bg-blue-500" data-testid="browse-marketplace-button">
                <ShoppingBag className="w-5 h-5 mr-2" />
                Browse Marketplace
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="watchlist-grid">
            {sortedWatchlist.map(card => (
              <div key={card.id} className="relative group">
                <CardListing 
                  card={card} 
                  isWatched={watchedIds.has(card.id)}
                  onWatchToggle={handleWatchToggle}
                  showPriceDelta={true}
                />
                {/* Price Alert Button */}
                <button
                  onClick={() => openAlertModal(card)}
                  className={`absolute top-2 right-12 p-2 rounded-lg transition-all z-10 ${
                    card.alert_enabled && (card.target_price || card.alert_on_any_drop)
                      ? 'bg-amber-500/20 border border-amber-500/30 text-amber-400'
                      : 'bg-zinc-800/80 border border-zinc-700 text-zinc-500 opacity-0 group-hover:opacity-100'
                  }`}
                  title={card.target_price ? `Alert at $${card.target_price}` : 'Set price alert'}
                  data-testid={`alert-btn-${card.id}`}
                >
                  {card.alert_enabled && (card.target_price || card.alert_on_any_drop) ? (
                    <Bell className="w-4 h-4" />
                  ) : (
                    <BellOff className="w-4 h-4" />
                  )}
                </button>
                {/* Target Price Badge */}
                {card.target_price && (
                  <div className="absolute top-14 right-2 px-2 py-1 bg-amber-500/20 border border-amber-500/30 rounded text-xs text-amber-400 font-medium z-10">
                    Alert: ${card.target_price}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Price Alert Modal */}
        <PriceAlertModal
          isOpen={alertModalOpen}
          onClose={() => {
            setAlertModalOpen(false);
            setSelectedCard(null);
          }}
          card={selectedCard}
          token={localStorage.getItem('token')}
          onUpdate={fetchWatchlist}
        />
      </main>
    </div>
  );
};

export default WatchlistPage;
